/**
 * MAHI — Signup (new users).
 *
 * Step 1 of onboarding: phone number only — the lowest-friction entry
 * point. Name, style, photo, and location are all collected later in the
 * onboarding flow itself (see Onboarding.jsx), not here.
 *
 * Stage 1 (collect): phone → POST /auth/signup/start
 * Stage 2 (verify): 4-digit OTP, with an auto-resend cooldown → POST /auth/signup/verify
 *
 * Google sign-in is available as an alternative path.
 *
 * A guest arriving here (from the try-on button, or the profile) goes through
 * /auth/claim/* instead of /auth/signup/*, and that is the only difference —
 * same screen, same two stages, same copy. The claim endpoints attach the
 * verified number to the account this person has already been using, so the
 * wardrobe they built while browsing is simply still there afterwards. Posting
 * to /auth/signup/verify instead would create a SECOND account and strand the
 * first, which is the one outcome this whole flow exists to prevent.
 *
 * Claiming also collapses the signup/login fork below into one call: unlike
 * /signup/start, /claim/start sends a code whether or not the number is
 * already known, and /claim/verify decides between promoting this account and
 * merging it into the existing one.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mahi, { errMsg, trackEvent } from '../api';
import { useMahiAuth } from '../auth';
import MahiRobot, { useMahiMood } from '../components/MahiRobot';
import MahiLogo from '../components/ui/MahiLogo';
import OtpResend from '../components/onboarding/OtpResend';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { warmDiscoverFeed } from '../prefetch';

export default function MahiSignup() {
  const navigate = useNavigate();
  const { user, refresh } = useMahiAuth();
  const isGuest = Boolean(user?.is_guest);
  const [stage, setStage] = useState('collect'); // 'collect' | 'verify'
  // Which flow this number actually turned out to be. Decided by the server on
  // the first send, not by which page the user happened to land on — see
  // `start` below.
  const [mode, setMode] = useState('signup'); // 'signup' | 'login'
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [otpError, setOtpError] = useState('');
  const { mood, setMood, flash } = useMahiMood();

  // Resend has to follow whichever flow we ended up in — re-posting to
  // /signup/start for a number that already has an account would just 409
  // again and never send anything.
  const sendOtp = async (forMode = mode) => {
    if (isGuest) {
      // One endpoint for both cases - it sends the code either way, and the
      // verify step works out whether this number is new.
      await mahi.post('/auth/claim/start', { phone: phone.trim() });
    } else if (forMode === 'login') {
      await mahi.post('/auth/login/start', { identifier: phone.trim() });
    } else {
      await mahi.post('/auth/signup/start', { phone: phone.trim() });
    }
  };

  const start = async () => {
    if (!phone.trim()) return toast.error('Please enter your mobile number');
    setBusy(true);
    setMood('thinking');
    try {
      let nextMode = 'signup';
      try {
        await sendOtp('signup');
        // A guest never takes the 409 branch below - /claim/start does not
        // refuse a known number - so the send above is the whole story.

        trackEvent('signup_started', { method: 'msg91' });
        toast.success('OTP sent to your mobile');
      } catch (e) {
        // 409 means this number already has an account. Rather than dead-ending
        // on "please log in" and making someone find the sign-in page and retype
        // the number they just typed, treat it as the login it clearly is and
        // send that OTP instead. /signup/start rejects before it sends anything,
        // so this is still exactly one SMS — no double-send, no wasted credit.
        if (e?.response?.status !== 409) throw e;
        nextMode = 'login';
        await sendOtp('login');
        trackEvent('login_started', { channel: 'phone', via: 'signup_recognised' });
        toast.success('Welcome back - code sent to your mobile');
      }
      setMode(nextMode);
      flash('celebrating');
      setStage('verify');
    } catch (e) { toast.error(errMsg(e, 'Could not send OTP')); flash('alert'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!phoneCode) return setOtpError('Enter the 4-digit code');
    setBusy(true);
    setMood('thinking');
    setOtpError('');
    try {
      let data;
      let isLogin = mode === 'login';
      if (isGuest) {
        ({ data } = await mahi.post('/auth/claim/verify', {
          phone: phone.trim(), code: phoneCode,
        }));
        // `merged` means the number already had an account and this guest
        // session was folded into it - a returning user, however they got here.
        isLogin = Boolean(data.merged);
      } else if (isLogin) {
        ({ data } = await mahi.post('/auth/login/verify', {
          identifier: phone.trim(), code: phoneCode,
        }));
      } else {
        ({ data } = await mahi.post('/auth/signup/verify', {
          phone: phone.trim(), phone_code: phoneCode,
        }));
      }
      localStorage.setItem('mira_token', data.token);
      // Fire now — resolved well before onboarding finishes. This form also
      // serves a returning user it recognised (`isLogin`), so go through
      // warmDiscoverFeed rather than prefetching blind: it reads their gender
      // off the cached user when this device has seen them before, and only
      // falls back to the women's default for a genuinely new account.
      // Onboarding starts the men's snapshot the moment step 1 says otherwise.
      warmDiscoverFeed();
      trackEvent('phone_verified');
      trackEvent(isLogin ? 'login_completed' : 'signup_completed',
        isLogin ? { channel: 'phone', via: 'signup_recognised' } : {});
      flash('celebrating');
      const user = await refresh();
      // Same landing rule either way: a returning user who never finished
      // onboarding still gets dropped back where they stopped.
      const step = user?.onboarding_step ?? 1;
      navigate(step >= 5 ? '/discover' : `/onboarding/${step}`);
    } catch (e) {
      const msg = errMsg(e, 'Verification failed');
      setOtpError(msg);
      toast.error(msg);
      flash('alert');
    }
    finally { setBusy(false); }
  };

  const googleSignIn = async (credential) => {
    setBusy(true);
    setMood('thinking');
    try {
      const { data } = await mahi.post('/auth/google', { credential });
      localStorage.setItem('mira_token', data.token);
      warmDiscoverFeed();
      trackEvent('signup_completed', { channel: 'google' });
      flash('celebrating');
      const user = await refresh();
      const step = user?.onboarding_step ?? 1;
      navigate(step >= 5 ? '/discover' : `/onboarding/${step}`);
    } catch (e) { toast.error(errMsg(e, 'Google sign-in failed')); flash('alert'); }
    finally { setBusy(false); }
  };

  return (
      <div className="min-h-screen bg-blush-100 text-slate-900 antialiased flex items-center justify-center px-5 py-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 26 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="text-center mb-10">
            <div className="flex justify-center mb-5"><MahiRobot size={96} mood={mood} /></div>
            <div className="flex justify-center text-slate-700">
              <MahiLogo className="h-[18px] w-auto" decorative />
            </div>
            <h1 className="mt-2 font-mahi-serif text-4xl text-slate-900 tracking-tight">
              {stage === 'collect'
                ? 'What’s your number?'
                : mode === 'login' ? 'Welcome back' : 'Enter your code'}
            </h1>
            <p className="mt-2 text-sm text-slate-500 font-mahi-sans">
              {stage === 'collect'
                ? 'Your first conversation with MAHI starts here.'
                : mode === 'login'
                  ? `We recognised ${phone} - enter the 4-digit code to sign in.`
                  : `We sent a 4-digit code to ${phone}`}
            </p>
          </div>

          <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.18)] p-7">
            {stage === 'collect' ? (
              <>
                <label className="block text-xs font-medium text-slate-500 mb-2 font-mahi-sans">Mobile number</label>
                <div className="relative mb-6">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="mahi-signup-phone-input" type="tel" inputMode="tel"
                    autoComplete="tel" enterKeyHint="go"
                    value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && start()}
                    className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3.5 font-mahi-sans focus:outline-none focus:ring-2 focus:ring-raspberry-200 focus:border-raspberry-300"
                    placeholder="+91 98765 43210"
                    autoFocus
                  />
                </div>

                <button
                  data-testid="mahi-signup-send-otp-btn"
                  onClick={start} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-raspberry-500 text-white px-6 py-3.5 font-medium font-mahi-sans hover:bg-raspberry-600 transition disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4" /></>}
                </button>

                <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-mahi-sans">
                  <span className="flex-1 h-px bg-slate-100" /> or <span className="flex-1 h-px bg-slate-100" />
                </div>
                <GoogleSignInButton onCredential={googleSignIn} />
              </>
            ) : (
              <>
                <label className="block text-xs font-medium text-slate-500 mb-2 font-mahi-sans">4-digit code</label>
                {/* autoComplete="one-time-code" is what makes iOS offer the SMS
                    straight from the keyboard bar (and Android autofill it)
                    rather than making someone leave the app, memorise four
                    digits and come back. type=tel, not number: number renders a
                    spinner and drops leading zeros. */}
                <input
                  data-testid="mahi-signup-phone-otp-input"
                  value={phoneCode}
                  onChange={(e) => { setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 4)); setOtpError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && verify()}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  enterKeyHint="go"
                  maxLength={4}
                  className={`w-full text-center tracking-[0.4em] text-2xl font-semibold rounded-xl border px-4 py-3.5 mb-2 font-mahi-sans focus:outline-none focus:ring-2 ${
                    otpError ? 'border-red-300 focus:ring-red-100' : 'border-slate-200 focus:ring-raspberry-200 focus:border-raspberry-300'
                  }`}
                  placeholder="••••"
                  autoFocus
                />
                {otpError && <p className="text-xs text-red-500 mb-3 font-mahi-sans" data-testid="otp-error">{otpError}</p>}

                <button
                  data-testid="mahi-signup-verify-btn"
                  onClick={verify} disabled={busy}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-full bg-raspberry-500 text-white px-6 py-3.5 font-medium font-mahi-sans hover:bg-raspberry-600 transition disabled:opacity-50 ${otpError ? '' : 'mt-3'}`}
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Continue'}
                </button>

                <div className="mt-4">
                  <OtpResend onResend={sendOtp} />
                </div>

                <button
                  data-testid="mahi-signup-edit-details-btn"
                  // Reset the flow too: the next number they type gets classified
                  // from scratch rather than inheriting this one's verdict.
                  onClick={() => { setStage('collect'); setOtpError(''); setPhoneCode(''); setMode('signup'); }}
                  className="w-full mt-3 text-xs text-slate-400 hover:text-slate-600 font-mahi-sans"
                >Change number</button>
              </>
            )}
          </div>

          <p className="text-center text-sm text-slate-500 mt-6 font-mahi-sans">
            Already have an account?{' '}
            <Link
              data-testid="mahi-go-to-login-link"
              to="/login"
              className="font-medium text-slate-900 hover:text-raspberry-600 transition"
            >Sign in</Link>
          </p>
          <p className="text-center text-xs text-slate-400 mt-3 font-mahi-sans">
            By continuing you agree to MAHI&apos;s{' '}
            <Link to="/terms" className="underline hover:text-slate-600">Terms</Link>{' '}
            and{' '}
            <Link to="/privacy-policy" className="underline hover:text-slate-600">Privacy Policy</Link>.
            A NanoKart product.
          </p>
        </motion.div>
      </div>
  );
}
